import { defineXray } from "../src/xray/manifest.js";
import { SHELL_TOOL_NAMES } from "../src/mine/shell.js";
import { extractProjectionOps, describeMissing } from "../extractors/projection/index.js";
import { collect, build, groupBy, anchor, ignore } from "../extractors/projection/spec.js";

export default defineXray({
  id: "projection",
  label: "State",
  title: "Scratchpad state an agent re-queried — the JSON artifacts it collected earlier in a session, "
    + "which keys it read back out of them, and what the answer was",

  // Carried from the judge's verdict rather than reworded, so the reason this lens
  // was built survives into the thing that ships.
  consumer: "someone reconstructing what an agent believed partway through a long task",
  decision: "whether the agent's later conclusions rest on state it actually re-checked, or on a stale file it never re-read",

  // TRACE-ONLY. The artifact looks like a target in the world, but it is one the
  // agent wrote itself minutes earlier: it does not outlive the session, so
  // grouping across sessions produced singletons (2 groups for 2 runs on a
  // 120-prompt window). Worse, a BASENAME is usually the only recoverable identity
  // — paths are built, not literal — so a tab would render forty unrelated
  // `state.json` reads as one artifact. Within a single trace the same grouping is
  // sound, because there the basename really does name one file.
  tab: false,
  tabReason: "the artifact is session-local scratch state the agent wrote itself, and "
    + "basename is the only recoverable identity, so grouping across sessions merges unrelated files",

  // Trace-only, so this does not down-select a tab of its own — it is how the
  // LINEAGE window finds the prompts carrying this lens's work. Same matcher
  // its tab-bearing siblings use: every op here is read off a shell command.
  substrate: { label: "shell (data projections)", match: (n) => SHELL_TOOL_NAMES.test(String(n).split("__").pop()) },

  feed: { domain: "projection" },
  extract: extractProjectionOps,
  collect,
  group: build,
  groupBy,
  anchor,
  ignore,

  // Lineage and the TAB disagree here, and both are right, because they ask
  // different questions. A tab groups across sessions, where a basename merges
  // unrelated files — hence `tab: false` above. The graph's scope is WITHIN a
  // session, and there the basename really does name one file: the same file some
  // earlier write created, which `fileNodes` already holds. This node is the read
  // that completes that pair, so excluding it removed one end of a derivation the
  // graph exists to find.
  lineage: {
    kind: "scratch",
    ref: (op) => op.artifacts?.[0] || null,
    // Re-reading state produces nothing; it informs whatever the agent did next.
    role: () => "consultation",
    text: (op) => op.output || "",
    label: (op) => `read ${op.artifacts?.slice().sort().join(" + ") || "scratch state"}`,
    intent: (op) => op.projection || null,
  },

  emptyNote: "No re-queried scratchpad state in this window",
  explainMissing: describeMissing,
});
